import React, { useContext, useMemo } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { Body as BodyBase } from 'js/design_system/Body';
import { DeprecatedBreakpoint } from 'js/design_system/DeprecatedBreakpoint';
import { NewsPreview } from 'js/social/shared/types';
import PaperContainerBase from './PaperContainer';
import { PostImage } from './shared/PostImage';
import { SocialEditor } from 'js/social/shared/SocialEditor/SocialEditor';
import useBreakpoint from 'js/utils/hooks/useDeprecatedBreakpoint';
import News from 'js/social/News/News';
import { FeedContext } from 'js/social/contexts/feed';
import _Container from "CollapsedActivityPreview-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=Container";
import _Name from "CollapsedActivityPreview-Name.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=Name";
import _ContentContainer from "CollapsedActivityPreview-ContentContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=ContentContainer";
import _LeftContainer from "CollapsedActivityPreview-LeftContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=LeftContainer";
import _QuotedPostContainer from "CollapsedActivityPreview-QuotedPostContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=QuotedPostContainer";
import _ImageContainer from "CollapsedActivityPreview-ImageContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=ImageContainer";
import _PaperContainer from "CollapsedActivityPreview-PaperContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=PaperContainer";
import _StatsBody from "CollapsedActivityPreview-StatsBody.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/CollapsedActivityPreview.tsx?styleId=StatsBody";


const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const Name = /*#__PURE__*/styled(BodyBase, null, {
  displayName: "Name",
  styles: _Name
});

const ContentContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ContentContainer",
  styles: _ContentContainer
});

const LeftContainer = /*#__PURE__*/styled("div", null, {
  displayName: "LeftContainer",
  styles: _LeftContainer
});

const QuotedPostContainer = /*#__PURE__*/styled("div", null, {
  displayName: "QuotedPostContainer",
  styles: _QuotedPostContainer
});

const ImageContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ImageContainer",
  styles: _ImageContainer
});

const PaperContainer = /*#__PURE__*/styled(PaperContainerBase, null, {
  displayName: "PaperContainer",
  styles: _PaperContainer
});

const StatsBody = /*#__PURE__*/styled(BodyBase, null, {
  displayName: "StatsBody",
  styles: _StatsBody
});

type Props = {
  displayName: string;
  preview: NewsPreview;
  onsitePodcastPromotion?: boolean;
};

export const CollapsedActivityPreview: React.FC<Props> = ({
  displayName,
  preview: {
    content,
    tags,
    hashtags,
    quotable,
    work,
    image,
    reason,
    numLikes,
    numComments,
  },
  onsitePodcastPromotion = false,
}) => {
  const likeText =
    numLikes > 0 ? `${numLikes} ${pluralize('like', numLikes)}` : null;
  const commentText =
    numComments > 0
      ? `${numComments} ${pluralize('comment', numComments)}`
      : null;
  const statsText = [likeText, commentText].filter(Boolean).join(`  •  `);
  const {
    userActions: { getUserData },
  } = useContext(FeedContext);
  const hasContent = !!content || tags.length > 0;
  const mobile = useBreakpoint() < DeprecatedBreakpoint.tablet;

  const imageComponent = useMemo(
    () =>
      image ? (
        <ImageContainer>
          <PostImage title={displayName} caption={content} image={image} />
        </ImageContainer>
      ) : null,
    [content, image, displayName],
  );

  return (
    <Container>
      {reason !== 'promotion' && (
        <Name bold>
          {displayName} {reason}
        </Name>
      )}

      <ContentContainer>
        <LeftContainer>
          {hasContent && (
            <SocialEditor
              readOnly
              collapsible
              content={content ?? ''}
              tags={tags}
              allowedHashtags={hashtags.map((hashtag) => hashtag.hashtag)}
              lines={2}
            />
          )}

          {(mobile || !hasContent) && imageComponent}

          {work && (
            <PaperContainer
              work={work}
              style={{ padding: 0 }}
              onsitePodcastPromotion={onsitePodcastPromotion}
            />
          )}

          {quotable && (
            <QuotedPostContainer>
              <News
                {...quotable}
                getUserData={getUserData}
                setUserFollowing={() => null}
                removeUserContent={() => null}
                removeFeedNews={() => null}
                addFeedNews={() => null}
                addFeedComment={() => null}
                updateFeedNews={() => null}
                likeFeedNews={() => null}
                unlikeFeedNews={() => null}
                repostFeedNews={() => null}
                unrepostFeedNews={() => null}
                updateWorkBookmarkStatus={() => null}
              />
            </QuotedPostContainer>
          )}

          {statsText && <StatsBody size="sm">{statsText}</StatsBody>}
        </LeftContainer>

        {!mobile && hasContent && imageComponent}
      </ContentContainer>
    </Container>
  );
};
